import {Box, Button, FormControl, IconButton, MenuItem, Select, TextField, Typography} from "@mui/material";
import React, {useEffect, useMemo, useRef, useState} from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {ColumnDef, Filter, FilterDef, FilterOperator, FilterType, FilterValue} from "./types";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {useTranslation} from "react-i18next";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {FilterSelectComponent} from "./FilterSelectComponent";
import {FilterChipInput} from "./Filter/FilterChipInput";

interface FilterEditorProps<T> {
    filterDefs: FilterDef<T>[];
    columnDefs: ColumnDef<T>[];
    filters: Filter[];
    addFilter?: Filter;
    triggerFocusLast: number;
}

export default function FilterEditor<T>({
    filterDefs,
    columnDefs,
    filters,
    addFilter,
    triggerFocusLast,
}: FilterEditorProps<T>) {
    const {t} = useTranslation();
    const columnDefByName = useMemo(() => {
        return columnDefs.reduce((acc, curr) => {
            acc.set(curr.fieldName, curr);
            return acc;
        }, new Map<"anywhere" | keyof T, ColumnDef<T>>());
    }, [columnDefs]);

    const [searchParams] = useSearchParams();
    const [data, setData] = useState(filters);
    const {hash} = useLocation();
    const navigate = useNavigate();
    const autoFocusRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (filters.length > 0 || data.length === 0) {
            setData(
                filters.length === 0
                    ? [
                          {
                              f: String(filterDefs[0].fieldName),
                              o: filterDefs[0].filterOperators[0],
                              v: "",
                          },
                      ]
                    : filters
            );
        }
    }, [JSON.stringify(filters), JSON.stringify(filterDefs)]);

    useEffect(() => {
        setTimeout(() => {
            if (autoFocusRef.current) {
                autoFocusRef.current.focus();
            }
        }, 100);
    }, [triggerFocusLast]);

    useEffect(() => {
        if (addFilter) {
            setData((current) => [...current, addFilter]);
        }
    }, [addFilter]);

    const handleFieldChanged = (idx: number, field: string) => {
        const newData = [...data];
        const filterDef = filterDefs.find((filter) => filter.fieldName === field);
        if (filterDef) {
            newData[idx].f = field;
            newData[idx].o = filterDef.filterOperators[0];
            newData[idx].v = "";
        } else {
            newData.splice(idx, 1);
        }
        setData(newData);
    };

    const handleOperatorChanged = (idx: number, operator: FilterOperator) => {
        const newData = [...data];
        newData[idx].o = operator;
        setData(newData);
    };

    const handleValueChanged = (idx: number, value: FilterValue, apply = false) => {
        const newData = [...data];
        if (Array.isArray(value)) {
            const castToStrArray = value as string[];
            value = castToStrArray.filter((v) => v !== "");
        }
        newData[idx].v = value;
        setData(newData);

        if (apply) {
            applyCurrentFilter(newData);
        }
    };

    const applyCurrentFilter = (filter: Filter[]) => {
        searchParams.set("page", "0");
        searchParams.set(
            "filter",
            JSON.stringify(
                filter
                    .map((f) => {
                        if (typeof f.v === "string") {
                            f.v = f.v.trim();
                        }

                        return f;
                    })
                    .filter((f) => {
                        if (Array.isArray(f.v)) {
                            return f.v.length > 0;
                        }
                        if ([FilterOperator.NEX, FilterOperator.EX].includes(f.o)) {
                            return true;
                        }
                        return f.v;
                    })
            )
        );
        navigate({
            hash: hash,
            search: searchParams.toString(),
        });
    };

    const handleSubmit = () => {
        applyCurrentFilter(data);
    };

    const handleDelete = (idx: number) => {
        const mustReload = data[idx].v !== "";
        const newData = [...data];
        newData.splice(idx, 1);
        setData(newData);

        if (mustReload) {
            applyCurrentFilter(newData);
        }
    };

    const handleAddFilter = () => {
        setData([
            ...data,
            {
                f: String(filterDefs[0].fieldName),
                o: filterDefs[0].filterOperators[0],
                v: "",
            },
        ]);
    };

    const lastIndex = data.length - 1;

    const addFilterButton = (
        <IconButton aria-label="filter" size="small" onClick={handleAddFilter}>
            <AddCircleIcon />
        </IconButton>
    );

    return (
        <form onSubmit={handleSubmit}>
            <Box sx={{px: 2, py: 1, minHeight: 70}}>
                {data.length === 0 && <Typography>Filter {addFilterButton}</Typography>}
                {data.map((filter, idx) => {
                    const filterDef = filterDefs.find((f) => f.fieldName === filter.f);
                    if (!filterDef) {
                        return null;
                    }
                    let valueElement: JSX.Element;
                    const inputRef = idx === lastIndex ? autoFocusRef : null;
                    switch (filterDef.type) {
                        case FilterType.MULTI_CHOICE_ENUM:
                            valueElement = (
                                <FilterSelectComponent<Array<string | number>>
                                    multiple
                                    inputRef={inputRef}
                                    onChange={(evt) => {
                                        handleValueChanged(idx, evt.target.value, true);
                                    }}
                                    value={Array.isArray(filter.v) ? filter.v : [filter.v]}
                                    enumValues={filterDef?.enumValues}
                                />
                            );
                            break;
                        case FilterType.ENUM:
                            valueElement = (
                                <FilterSelectComponent<string>
                                    inputRef={inputRef}
                                    onChange={(evt) => handleValueChanged(idx, evt.target.value, true)}
                                    value={Array.isArray(filter.v) ? (filter?.v?.[0] as string) : (filter.v as string)}
                                    enumValues={filterDef?.enumValues}
                                />
                            );
                            break;
                        case FilterType.DATE:
                        case FilterType.DATE_TIME: {
                            const ofTypeDate = filterDef.type === FilterType.DATE;
                            const inputFormat = ofTypeDate ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm";
                            valueElement = (
                                <DatePicker
                                    inputRef={inputRef}
                                    value={typeof filter.v === "string" && filter.v ? dayjs(filter.v) : null}
                                    mask={ofTypeDate ? "____-__-__" : "____-__-__ __:__"}
                                    inputFormat={inputFormat}
                                    onChange={(newValue: dayjs.Dayjs | null) => {
                                        const granularity = ofTypeDate ? "day" : "minute";
                                        handleValueChanged(
                                            idx,
                                            (filter.o === FilterOperator.LTE
                                                ? newValue?.endOf(granularity)
                                                : newValue
                                            )?.format(inputFormat) || ""
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField margin={"dense"} hiddenLabel size={"small"} {...params} />
                                    )}
                                />
                            );
                            break;
                        }
                        case FilterType.BOOLEAN:
                            valueElement = (
                                <FormControl margin={"dense"} sx={{minWidth: 145, mr: 1}} size="small">
                                    <Select
                                        inputRef={inputRef}
                                        value={filter.v}
                                        onChange={(evt) => handleValueChanged(idx, evt.target.value, true)}
                                    >
                                        {[
                                            {labelKey: "generic_yes", value: "true"},
                                            {labelKey: "generic_no", value: "false"},
                                        ].map((ev) => (
                                            <MenuItem key={ev.value} value={ev.value}>
                                                {t(ev.labelKey)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                            break;
                        default:
                            if (filter.o === FilterOperator.NEX || filter.o === FilterOperator.EX) {
                                valueElement = <div></div>;
                                break;
                            }
                            valueElement =
                                filter.o === FilterOperator.IN ? (
                                    <FilterChipInput
                                        margin={"dense"}
                                        hiddenLabel
                                        id="outlined-size-small"
                                        initialValues={
                                            Array.isArray(filter.v)
                                                ? (filter.v as string[])
                                                : isString(filter.v) && filter.v.length > 0
                                                ? [filter.v]
                                                : []
                                        }
                                        onChange={(values) =>
                                            handleValueChanged(idx, values && values.length ? values : "")
                                        }
                                        size="small"
                                        type={filterDef.type === FilterType.NUMBER ? "number" : undefined}
                                        inputRef={inputRef}
                                        maxVisibleChips={2}
                                    />
                                ) : (
                                    <TextField
                                        margin={"dense"}
                                        hiddenLabel
                                        id="outlined-size-small"
                                        value={filter.v}
                                        onChange={(evt) => handleValueChanged(idx, evt.target.value, false)}
                                        onKeyDown={(evt) => {
                                            if (evt.key === "Enter") applyCurrentFilter(data);
                                        }}
                                        size="small"
                                        type={filterDef.type === FilterType.NUMBER ? "number" : undefined}
                                        inputRef={inputRef}
                                    />
                                );
                            break;
                    }

                    return (
                        <Box key={idx} sx={{display: "flex", alignItems: "center"}}>
                            <FormControl margin={"dense"} sx={{minWidth: 165, mr: 1}} size="small">
                                <Select value={filter.f} onChange={(evt) => handleFieldChanged(idx, evt.target.value)}>
                                    {filterDefs.map((fd) => (
                                        <MenuItem key={String(fd.fieldName)} value={String(fd.fieldName)}>
                                            {fd.label ? fd.label : columnDefByName.get(fd.fieldName)?.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl margin={"dense"} sx={{minWidth: 145, mr: 1}} size="small">
                                <Select
                                    value={filter.o}
                                    onChange={(evt) => handleOperatorChanged(idx, evt.target.value as FilterOperator)}
                                >
                                    {filterDef?.filterOperators.map((op) => (
                                        <MenuItem key={op} value={op}>
                                            {t("generic_filterOperator" + op.toUpperCase())}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {valueElement}
                            {data.length > 0 && (
                                <IconButton onClick={() => handleDelete(idx)}>
                                    <DeleteIcon />
                                </IconButton>
                            )}
                            {idx === lastIndex && addFilterButton}
                            {idx === 0 && (
                                <Button sx={{ml: 1}} variant="contained" size="small" onClick={handleSubmit}>
                                    {t("action_apply")}
                                </Button>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </form>
    );
}

function isString(val: unknown): val is string {
    return typeof val === "string";
}
